import React, { memo, useContext, useState } from 'react'
import classNames from 'classnames'
import moment from 'moment-timezone'
import { Avatar } from 'transfix-ui/components/Avatar'
import { IconButton } from 'transfix-ui/components/IconButton'
import { OverflowMenu } from 'transfix-ui/components/OverflowMenu'
import { TableRow, TableCell } from 'transfix-ui/components/Table'
import { Tooltip } from 'transfix-ui/components/Tooltip'
import { makeStyles } from 'transfix-ui/core'
import Box from 'transfix-ui/core/Box'
import { FeatureFlagsContext } from 'utils/context/FeatureFlagsContext'
import {
  NormFileGeneratorStatus,
  useGetWorkStatus,
  GetWorkStatusResponse,
  useRerunFailedLanes,
} from 'apps/marketplace/apiTypes/normFileGeneratorStatuses'
import { EllipsisIcon, SwitchIcon, RightArrowIcon } from 'transfix-icons'
import { FORMATS } from 'constants/datetime'
import { DISPLAY } from 'constants/display'
import { StatusTag } from './StatusTag'
import { WorkspaceStatusTag } from './WorkspaceStatusTag'

interface INormingJobsTableRowProps {
  normingJob: NormFileGeneratorStatus
  onRetrySuccess: (toastMessage: string) => void
  onRetryError: (toastMessage: string) => void
}

const useStyles = makeStyles({
  noDisplay: {
    display: 'none !important',
  },
})

export const NormingJobsTableRow = memo<INormingJobsTableRowProps>(
  ({ normingJob, onRetryError, onRetrySuccess }) => {
    const {
      id,
      createdAt,
      shipper = '',
      rfpName = '',
      googleSheetsUrl,
      status,
      requestForProposalId,
      createdByUser = {},
      dueDate = '',
      auditWorkspace,
      workspace,
      rfpPricingRound,
    } = normingJob
    const { firstName, lastName } = createdByUser
    const userName = firstName || lastName ? `${firstName} ${lastName}` : null
    const { datetimeShort12H: dateFormat, dayShort: dayShortFormat } = FORMATS
    const [isExpanded, setIsExpanded] = useState(false)
    const { mutate: rerunFailedLanes } = useRerunFailedLanes({})
    const { data: workStatus, error, loading, refetch } = useGetWorkStatus({
      queryParams: {
        id,
      },
      lazy: true,
    })

    const classes = useStyles()

    const { getFlag } = useContext(FeatureFlagsContext)
    const { enabled: displayRfpStatusTag } = getFlag('displayRfpStatusTag')

    const formatWorkStatus = (statusData: GetWorkStatusResponse | null): string => {
      if (statusData?.total === null) return 'No data'
      return `${statusData?.success || 0} of ${statusData?.total} lanes normed`
    }

    const tableClass = classNames({ [classes.noDisplay]: !displayRfpStatusTag })

    async function retry() {
      try {
        await rerunFailedLanes({ id })
        onRetrySuccess(`Job ${id} retry successful.`)
      } catch (err) {
        const message = err.data?.errors[0] || err.message
        onRetryError(message)
      }
    }

    const overflowOptions = [
      {
        itemComponent: (
          <div className='flex flex-row items-center'>
            <SwitchIcon size='large' />
            <span className='ml2'>Retry</span>
          </div>
        ),
        label: 'retry',
        action: () => retry(),
      },
    ]

    return (
      <TableRow dataTest={`norm-${id}`} highlightOnHover>
        <TableCell dataTest={`norm-${id}-id`}>{id}</TableCell>
        <TableCell dataTest={`norm-${id}-rfpId`}>{requestForProposalId || DISPLAY.empty}</TableCell>
        <TableCell dataTest={`norm-${id}-createdAt`}>
          {createdAt ? moment(createdAt).format(dateFormat) : DISPLAY.empty}
        </TableCell>
        <TableCell dataTest={`norm-${id}-shipper`}>{shipper || DISPLAY.empty}</TableCell>
        <TableCell dataTest={`norm-${id}-rfpName`}>{rfpName || DISPLAY.empty}</TableCell>
        <TableCell dataTest={`norm-${id}-googleSheetsUrl`}>
          <a href={googleSheetsUrl} target='_blank' rel='noopener noreferrer'>
            Google Sheets URL
          </a>
        </TableCell>
        <TableCell dataTest={`norm-${id}-status`}>
          {status === 'pending' || status === 'error' ? (
            <Tooltip
              position='bottom'
              trigger={
                <span onMouseEnter={() => refetch()}>
                  <StatusTag status={status} />
                </span>
              }>
              {loading ? 'Checking status...' : error?.message || formatWorkStatus(workStatus)}
            </Tooltip>
          ) : (
            <StatusTag status={status} />
          )}
        </TableCell>
        <TableCell dataTest={`norm-${id}-creator`}>
          {userName ? (
            <Box display='flex' alignItems='center'>
              <Box mr={1}>
                <Avatar name={userName} />
              </Box>
              <Box flexGrow={1}>{userName}</Box>
            </Box>
          ) : (
            DISPLAY.empty
          )}
        </TableCell>
        <TableCell dataTest={`norm-${id}-due-date`}>
          {dueDate ? moment(dueDate).format(dayShortFormat) : DISPLAY.empty}
        </TableCell>
        <TableCell dataTest={`norm-${id}-workspace-status`} className={tableClass}>
          {auditWorkspace && displayRfpStatusTag && (
            <WorkspaceStatusTag status={workspace} pricingRound={rfpPricingRound} />
          )}
        </TableCell>
        <TableCell dataTest={`norm-${id}-options`}>
          <OverflowMenu
            onExpanded={setIsExpanded}
            expanded={isExpanded}
            dropdownId={`norm-${id}-overflow`}
            trigger={
              <IconButton
                Icon={EllipsisIcon}
                label='options'
                data-test={`norm-${id}-options-icon`}
                onClick={() => setIsExpanded(true)}
                disabled={status === 'success'}
                size='large'
              />
            }
            options={overflowOptions}
            dataTest={`norm-${id}-overflow`}
          />
        </TableCell>
        <TableCell>
          <Tooltip
            position='bottom'
            trigger={
              <span onMouseEnter={() => refetch()}>
                <IconButton
                  Icon={RightArrowIcon}
                  disabled={auditWorkspace === false || status !== 'success'}
                  onClick={() => {
                    window.open(`${__ROOT__}/rfp/${requestForProposalId}`)
                  }}
                  data-test={`norm-${id}-dashboard-link-icon`}
                  size='large'
                />
              </span>
            }
            disable={auditWorkspace === true && status === 'success'}>
            {auditWorkspace === false || status !== 'success' ? 'Dashboard not available' : null}
          </Tooltip>
        </TableCell>
      </TableRow>
    )
  }
)
